import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Sparkles } from "lucide-react";
import { useState } from "react";

interface Diagnosis {
  id?: string;
  code?: string; // CID-10
  description: string;
  type: "primary" | "secondary";
}

interface DiagnosisSectionProps {
  diagnoses: Diagnosis[];
  onChange: (diagnoses: Diagnosis[]) => void;
  onSuggestDiagnosis?: () => Promise<void>;
  isSuggesting?: boolean;
}

export function DiagnosisSection({
  diagnoses,
  onChange,
  onSuggestDiagnosis,
  isSuggesting = false,
}: DiagnosisSectionProps) {
  const [newDiagnosis, setNewDiagnosis] = useState<Diagnosis>({
    description: "",
    type: "primary",
  });

  const addDiagnosis = () => {
    if (newDiagnosis.description.trim()) {
      const primaryCount = diagnoses.filter((d) => d.type === "primary").length;
      const diagnosis: Diagnosis = {
        ...newDiagnosis,
        type: primaryCount === 0 ? "primary" : "secondary",
      };
      onChange([...diagnoses, diagnosis]);
      setNewDiagnosis({ description: "", type: "primary" });
    }
  };

  const removeDiagnosis = (index: number) => {
    onChange(diagnoses.filter((_, i) => i !== index));
  };

  const updateDiagnosis = (index: number, field: keyof Diagnosis, value: string) => {
    const updated = [...diagnoses];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const primaryDiagnosis = diagnoses.find((d) => d.type === "primary");
  const secondaryDiagnoses = diagnoses.filter((d) => d.type === "secondary");

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Diagnóstico</CardTitle>
          {onSuggestDiagnosis && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onSuggestDiagnosis}
              disabled={isSuggesting}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {isSuggesting ? "Gerando..." : "Sugerir com IA"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Diagnóstico Principal */}
        <div className="space-y-2">
          <Label>Diagnóstico Principal</Label>
          {primaryDiagnosis ? (
            <div className="flex items-start gap-2 p-3 border rounded-lg bg-primary/5 transition-all hover:bg-primary/10">
              <div className="flex-1 space-y-2">
                <Input
                  placeholder="CID-10 (opcional)"
                  value={primaryDiagnosis.code || ""}
                  onChange={(e) => {
                    const index = diagnoses.findIndex((d) => d.type === "primary");
                    updateDiagnosis(index, "code", e.target.value);
                  }}
                  className="text-xs"
                />
                <Textarea
                  placeholder="Descreva o diagnóstico principal"
                  value={primaryDiagnosis.description}
                  onChange={(e) => {
                    const index = diagnoses.findIndex((d) => d.type === "primary");
                    updateDiagnosis(index, "description", e.target.value);
                  }}
                  rows={2}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const index = diagnoses.findIndex((d) => d.type === "primary");
                  removeDiagnosis(index);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="p-3 border border-dashed rounded-lg text-center text-muted-foreground text-sm">
              Nenhum diagnóstico principal definido
            </div>
          )}
        </div>

        {/* Diagnósticos Secundários */}
        {secondaryDiagnoses.length > 0 && (
          <div className="space-y-2">
            <Label>Diagnósticos Secundários</Label>
            <div className="space-y-2">
              {secondaryDiagnoses.map((diagnosis, index) => {
                const actualIndex = diagnoses.findIndex((d) => d === diagnosis);
                return (
                  <div
                    key={actualIndex}
                    className="flex items-start gap-2 p-3 border rounded-lg"
                  >
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="CID-10 (opcional)"
                        value={diagnosis.code || ""}
                        onChange={(e) => updateDiagnosis(actualIndex, "code", e.target.value)}
                        className="text-xs"
                      />
                      <Textarea
                        placeholder="Descreva o diagnóstico secundário"
                        value={diagnosis.description}
                        onChange={(e) =>
                          updateDiagnosis(actualIndex, "description", e.target.value)
                        }
                        rows={2}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDiagnosis(actualIndex)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Adicionar Novo Diagnóstico */}
        <div className="space-y-2 p-3 border border-dashed rounded-lg">
          <Label>Adicionar Diagnóstico</Label>
          <div className="space-y-2">
            <Input
              placeholder="CID-10 (opcional)"
              value={newDiagnosis.code || ""}
              onChange={(e) =>
                setNewDiagnosis({ ...newDiagnosis, code: e.target.value })
              }
              className="text-xs"
            />
            <Textarea
              placeholder="Descreva o diagnóstico"
              value={newDiagnosis.description}
              onChange={(e) =>
                setNewDiagnosis({ ...newDiagnosis, description: e.target.value })
              }
              rows={2}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addDiagnosis}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
